import {all, call, delay, put, select, takeEvery, takeLatest} from 'redux-saga/effects'
import {push, replace} from 'react-router-redux'
import {get, has, isNil} from 'lodash'
import {getFingerprint} from "@thumbmarkjs/thumbmarkjs"
import types from '../actions/types'
import {generalError} from '../actions'
import {getFailureCode, getFailureReason} from 'helpers/api'
import {getChangePlanUrl, urls} from 'helpers/url'
import {apiErrorCodes, AUTHORISATION_LEVEL} from 'helpers/constants'
import {getRefreshAuthToken, getUser} from 'selectors/auth'
import {openTfaSetup, setLocale} from 'actions/settings'
import {getSessionId, getSystemName} from 'selectors/data'
import {connect} from 'actions/socket'
import {snackbarMessage} from 'actions/snackbar'
import {iconNames} from 'components/icon'
import {getRedirectUrl, normalizePathname} from "helpers/navigation"
import {getEnvironmentPrefix, isDev} from "helpers/system"
import {
  getAccountTypeName,
  isExpired,
  parseUserName,
  sessionIsAuthenticationLevel,
  shouldShowEnableTfa,
} from 'helpers/user'
import {formatDate} from 'helpers/dates'
import {getPlanNamesFromPackages} from 'helpers/plans'
import {
  activateUserFailure,
  activateUserSuccess,
  activateUserWithJwtFailure,
  activateUserWithJwtSuccess,
  authenticateTotpLoginFailure,
  authenticateTotpLoginSuccess,
  hideTfaLoginForm,
  loginFailure,
  loginSuccess,
  logoutAttempt,
  logoutSuccess,
  refreshAuthFailure,
  refreshAuthSuccess,
  refreshAuthTotpFailure,
  refreshAuthTotpRequired,
  refreshAuthTotpSuccess,
  resetPasswordFailure,
  resetPasswordSuccess,
  sessionLoginFailure,
  sessionLoginSuccess,
  showTfaLoginForm,
  signUpFailure,
  signUpSuccess,
  updateUser,
  userDataReadSuccess,
} from 'actions/auth'

const WEB_SOCKET_MAXIMUM_CONNECT_TRIES = 10

// Note we are supplying the api as a variable, we can swap this out to a fixture for testing
// or progressively upgrade the api (websockets for example)
export default (api) => {

  function* signUp({email, password, companyName, name, countryName, countryCode, phone, locale, industry, token}) {
    try {
      const response = yield call(
        api.signUp_202011,
        email,
        password,
        companyName,
        name,
        countryName,
        countryCode,
        phone,
        industry,
        token
      )
      if (response.ok) {
        const user = get(response, 'data', {})

        if (!isExpired(user)) {
          yield put(signUpSuccess(user))
          yield put(push(getRedirectUrl(user)))
        } else {
          yield put(signUpSuccess(user))
          yield put(setLocale(locale))
        }

        const systemName = yield select(getSystemName)
        initializePendo(user, systemName)
        yield call(recordLogin, email, password)
      } else {
        yield put(signUpFailure(getFailureReason(response), getFailureCode(response)))
      }

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* logUserIn({username, password, token}) {
    try {
      const deviceFingerprint = yield getFingerprint()
      const response = yield call(api.login, username, password, deviceFingerprint, token)
      yield call(logInResponse, response)
      if (response.ok) yield call(recordLogin, username, password)

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* authenticateTfa({code, fingerPrint, isTrustDevice}) {
    try {
      const response = yield call(api.authenticateTfaWithTotp, code, fingerPrint, isTrustDevice)
      if (response.ok) {
        yield put(authenticateTotpLoginSuccess())
        yield call(logInResponse, response)
      } else {

        const failureReason = getFailureReason(response)
        const failureCode = getFailureCode(response)
        if (failureCode === apiErrorCodes.TOTP_CODE_INVALID) {
          yield put(authenticateTotpLoginFailure())
        } else {
          //redirect back to username password
          yield put(hideTfaLoginForm())
          yield put(loginFailure(failureReason, failureCode))
        }
      }

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* resetPassword({password, token}) {
    try {
      const response = yield call(api.resetPasswordUsingJWT, token, password)

      if (response.ok) {
        yield put(resetPasswordSuccess())
      } else {
        yield put(resetPasswordFailure(getFailureReason(response), getFailureCode(response)))
      }

      if (has(response, 'data.authorisationLevel')) {
        yield call(logInResponse, response)
        if (response.ok) yield call(recordLogin, get(response, 'data.username'), password)
      }

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  // TODO: remove with auth service feature toggle
  function* activateUser({username, forename, surname, password, code, id}) {
    try {
      const response = yield call(api.activateUser, username, forename, surname, password, code, id)

      if (response.ok) {
        const redirectUrl = get(response, 'data.redirectUrl', null)
        if (!isNil(redirectUrl)) {
          window.location.href = redirectUrl
          return
        }
        yield put(activateUserSuccess())
      } else {
        yield put(activateUserFailure(getFailureReason(response), getFailureCode(response)))
      }
      yield call(logInResponse, response)
      if (response.ok) yield call(recordLogin, username, password)

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* activateUserWithJwt({username, forename, surname, password, token}) {
    try {
      const response = yield call(api.activateUserWithJwt, token, username, forename, surname, password)

      if (response.ok) {
        const redirectUrl = get(response, 'data.redirectUrl', null)
        if (!isNil(redirectUrl)) {
          window.location.href = redirectUrl
          return
        }
        yield put(activateUserWithJwtSuccess())
      } else {
        yield put(activateUserWithJwtFailure(getFailureReason(response), getFailureCode(response)))
      }
      yield call(logInResponse, response)
      if (response.ok) yield call(recordLogin, username, password)

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* logInResponse(response) {
    if (response.ok) {
      if (sessionIsAuthenticationLevel(response, AUTHORISATION_LEVEL.PARTIALLY_AUTHENTICATED)) {
        yield put(showTfaLoginForm())
      } else {
        const user = get(response, 'data', null)
        yield put(loginSuccess(user))
        const systemName = yield select(getSystemName)
        initializePendo(user, systemName)
        if (shouldShowEnableTfa(user)) {
          yield put(openTfaSetup(true))
        }
        yield put(push(getRedirectUrl(user)))
      }
    } else {
      const failureReason = getFailureReason(response)
      const failureCode = getFailureCode(response)
      if (failureCode === apiErrorCodes.ACCOUNT_SIGN_UP) {
        yield put(signUpSuccess(get(response, 'data.data.auth')))
        yield put(push(urls.auth.signUp))
      } else {
        yield put(loginFailure(failureReason, failureCode))
      }
    }
  }

  function initializePendo(user, systemName = '') {
    try {
      if (window.pendo && user && !isNil(user?.id) && !isNil(user?.accountId)) {
        const {forename, surname} = parseUserName(get(user, 'name', ''))
        const accountTypeName = getAccountTypeName(get(user, 'accountTypeId', null))
        const planName = getPlanNamesFromPackages(get(user, 'packages', []))

        const environmentPrefix = getEnvironmentPrefix(systemName)
        const visitorId = `${environmentPrefix}${get(user, 'username', null)}`
        const accountId = `${environmentPrefix}${get(user, 'accountId', null)}`

        const createdDate = get(user, 'createdDate', null)
        const trialEndDate = get(user, 'trialEndDate', null)
        const trialStartDate = get(user, 'trialStartDate', null)
        const cancellingDate = get(user, 'cancellingDate', null)


        window.pendo.initialize({
          visitor: {
            id: visitorId,
            CG_email: get(user, 'username', null),
            CG_forename: get(user, 'forename', null) || forename,
            CG_surname: get(user, 'surname', null) || surname,
            CG_role: get(user, 'levelLabel', ''),
          },
          account: {
            id: accountId,
            CG_account_created_date: !isNil(createdDate) ? formatDate(createdDate, 'YYYY-MM-DD') : '',
            CG_free_trial_start_date: !isNil(trialStartDate) ? formatDate(trialStartDate, 'YYYY-MM-DD') : '',
            CG_free_trial_end_date: !isNil(trialEndDate) ? formatDate(trialEndDate, 'YYYY-MM-DD') : '',
            CG_company_name: get(user, 'accountName', null),
            CG_shop_address_line_1: get(user, 'homeCompanyAddress.address1', ''),
            CG_shop_address_line_2: get(user, 'homeCompanyAddress.address2', ''),
            CG_shop_address_city: get(user, 'homeCompanyAddress.city', ''),
            CG_shop_address_postal: get(user, 'homeCompanyAddress.postCode', ''),
            CG_shop_address_country: get(user, 'homeCompanyAddress.country', ''),
            CG_shop_address_phone: get(user, 'homeCompanyAddress.phone', ''),
            CG_churn_date: !isNil(cancellingDate) ? formatDate(cancellingDate, 'YYYY-MM-DD') : '',
            CG_billing_email: get(user, 'masterEmail', null),
            CG_account_type: accountTypeName,
            CG_plan_name: planName,
            CG_billing_recur_type: get(user, 'billingRecurType', null),
            CG_payment_type: get(user, 'paymentType', null),
            CG_subscription_status: get(user, 'subscriptionStatus', null),
          },
        })
      }

    } catch (e) {
      console.error(e)
    }
  }

  function* recordLogin(username, password) {
    try {
      if (!isDev())
        return

      const logins = JSON.parse(window.localStorage.getItem('previousLogins')) || []

      const newLogins = [
        {username, password, date: new Date().getTime()},
        ...logins
          .filter(login => login.username !== username)
          .slice(0, 9),
      ]

      window.localStorage.setItem('previousLogins', JSON.stringify(newLogins))


    } catch (e) {
      console.error(e)
    }
  }

  function* refreshAuth({password}) {
    try {
      const user = yield select(getUser)
      const username = get(user, 'username', null)

      const response = yield call(api.refreshAuth, username, password)
      if (response.ok) {
        const user = get(response, 'data', null)
        yield put(refreshAuthSuccess(user))
      } else {
        const code = getFailureCode(response)
        if (code === apiErrorCodes.TOTP_REQUIRED) {
          const token = get(response, 'data.data.token', null)
          yield put(refreshAuthTotpRequired(token))
        } else {
          yield put(refreshAuthFailure(getFailureReason(response), code))
        }
      }

    } catch (e) {
      console.error(e)
      yield put(refreshAuthFailure(e.message))
    }
  }

  function* refreshAuthTotp({totpCode}) {
    try {
      const token = yield select(getRefreshAuthToken)

      const response = yield call(api.refreshAuthTotp, token, totpCode)
      if (response.ok) {
        const user = get(response, 'data', null)
        yield put(refreshAuthTotpSuccess(user))
      } else {
        yield put(refreshAuthTotpFailure(getFailureReason(response), getFailureCode(response)))
      }

    } catch (e) {
      console.error(e)
      yield put(refreshAuthFailure(e.message))
    }
  }

  function* userReadWorker() {
    try {
      const result = yield call(api.userLoad)
      if (result.ok) {
        const user = result.data
        yield put(userDataReadSuccess(user))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* sessionLoginAttemptWorker() {
    try {
      const response = yield call(api.sessionLogin)
      if (response.ok && response.data) {
        const {success, user, errorCode} = response.data

        if (success) {
          yield put(sessionLoginSuccess(user))
          yield put(loginSuccess(user))

          const systemName = yield select(getSystemName)
          initializePendo(user, systemName)

          const pathname = window.location.pathname
          if (normalizePathname(pathname) === urls.auth.login) {
            const redirectUrl = getRedirectUrl(user)
            yield put(push(redirectUrl))
          }

        } else {
          if (user && errorCode === apiErrorCodes.ACCOUNT_SIGN_UP)
            yield put(signUpSuccess(user))

          yield put(sessionLoginFailure())
        }
      } else {
        yield put(sessionLoginFailure())
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* logoutAttemptWorker({message}) {
    yield call(api.logout)

    message = isNil(message) ? '' : message
    yield put(logoutSuccess(message))

    // Reloading the browser here to clear the state and avoid a web-socket disconnection issue
    window.location.href = urls.auth.login
  }

  function* websocketDisconnected({userDisconnected, closeEvent, tries}) {
    if (!userDisconnected) {
      const reason = get(closeEvent, 'reason')

      if (tries >= WEB_SOCKET_MAXIMUM_CONNECT_TRIES) {
        console.error(`Websocket connection was lost permanently, logging out. Reason: ${reason}`)
        yield put(logoutAttempt())

      } else {
        console.error(`Websocket connection was lost, retrying connection after 1 second. Reason: ${reason}`)
        yield delay(1000)
        const sessionId = yield select(getSessionId)
        yield put(connect(sessionId))
      }
    }
  }

  function* websocketNotAuthorised() {
    console.error('Websocket connection not authenticated')  // don't broadcast this to the user
    yield put(logoutAttempt())
  }

  function* upgradeAccount() {
    const user = yield select(getUser)
    yield put(push(getChangePlanUrl(user)))
  }

  function* userUpdated({user, updatedById, updatedByName}) {
    if (location.pathname === urls.myAccount.changePlan)
      return

    yield put(updateUser(user))

    const currentUser = yield select(getUser)
    if (updatedById !== currentUser.id) {

      const name = isNil(updatedByName)
        ? 'an Administrator'
        : updatedByName

      yield put(snackbarMessage(
        `Your access was changed by ${name}`,
        iconNames.info,
        false))
    }
  }

  function* closeTfaSetup() {
    const user = yield select(getUser)
    const redirectUrl = getRedirectUrl(user, false)
    yield put(replace(redirectUrl))
  }

  function* watcher() {
    yield all([
      takeLatest(types.SIGN_UP_ATTEMPT, signUp),
      takeLatest(types.LOGIN_ATTEMPT, logUserIn),
      takeLatest(types.REFRESH_AUTH_ATTEMPT, refreshAuth),
      takeLatest(types.REFRESH_AUTH_TOTP_ATTEMPT, refreshAuthTotp),
      takeLatest(types.AUTHENTICATE_TOTP_LOGIN_ATTEMPT, authenticateTfa),
      takeLatest(types.RESET_PASSWORD_ATTEMPT, resetPassword),
      takeLatest(types.ACTIVATE_USER_ATTEMPT, activateUser),
      takeLatest(types.ACTIVATE_USER_JWT_ATTEMPT, activateUserWithJwt),
      takeLatest(types.USER_DATA_READ_ATTEMPT, userReadWorker),
      takeLatest(types.SESSION_LOGIN_ATTEMPT, sessionLoginAttemptWorker),
      takeEvery(types.LOGOUT_ATTEMPT, logoutAttemptWorker),
      takeLatest(types.WS_NOT_AUTHORISED, websocketNotAuthorised),
      takeLatest(types.DISCONNECTED, websocketDisconnected),
      takeLatest(types.ACCOUNT_UPGRADE, upgradeAccount),
      takeLatest(types.USERS_USER_UPDATED, userUpdated),
      takeLatest(types.CLOSE_TFA_SETUP, closeTfaSetup),
    ])
  }

  return {
    watcher,
  }
}
